import { StoreLayout } from "../../components/StoreLayout";

const Section = ({ n, title, children }) => (
  <section className="mt-8" data-testid={`cust-terms-section-${n}`}>
    <h2 className="font-[Outfit] text-xl md:text-2xl font-bold text-[#1E293B]">{n}. {title}</h2>
    <div className="mt-2 space-y-3 text-[#475569] leading-relaxed text-[15px]">{children}</div>
  </section>
);

export default function CustomerTerms() {
  return (
    <StoreLayout>
      <div className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-3xl mx-auto px-5 py-12">
          <p className="uppercase text-xs tracking-[0.2em] font-semibold text-[#4F46E5]">Customer Agreement</p>
          <h1 className="font-[Outfit] text-4xl md:text-5xl font-extrabold tracking-tight mt-2">Customer Terms &amp; Conditions</h1>
          <p className="text-[#475569] mt-3">Last updated: June 2026</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <p className="text-[#475569] leading-relaxed text-[15px]">
          These Customer Terms &amp; Conditions ("Terms") apply when you register an account, book a service, or
          otherwise use Fixigo Home Services ("Fixigo", "we", "us"). By registering or placing a booking, you confirm
          that you have read, understood and agree to these Terms.
        </p>

        <Section n="1" title="Services">
          <p>Fixigo connects you with verified independent service professionals ("Partners") for home services such as
          appliance repair, installation, cleaning, plumbing, electrical work and pest control. We act as a facilitator;
          the service is performed by the assigned Partner. Service availability, slots and timelines are indicative and
          may vary based on Partner availability and your location.</p>
        </Section>

        <Section n="2" title="Payments">
          <p>All prices are shown in Indian Rupees (₹) and are inclusive of applicable taxes unless stated otherwise.
          Fixigo operates on a <strong>pay-after-service</strong> model — you pay once the service is completed. Charges
          for spare parts, materials or additional work will be communicated to you and agreed before such work is
          carried out.</p>
        </Section>

        <Section n="3" title="Cancellations &amp; Cancellation Charges">
          <p>You can cancel a booking from your Fixigo account before the service begins. The following cancellation
          policy applies:</p>
          <ul className="list-disc pl-6 space-y-2 mt-2">
            <li data-testid="cancel-policy-free"><strong>Free cancellation:</strong> Up to <strong>2 hours before</strong> the scheduled service time.</li>
            <li data-testid="cancel-policy-fee"><strong>₹99 cancellation fee:</strong> If you cancel <strong>within 2 hours</strong> of the scheduled service.</li>
            <li data-testid="cancel-policy-visit"><strong>Inspection / visit charge:</strong> Applicable if the technician has <strong>already arrived</strong> at your location.</li>
            <li data-testid="cancel-policy-nofee"><strong>No cancellation fee:</strong> If the cancellation is due to technician unavailability or operational issues from our side.</li>
          </ul>
          <p>A cancellation reason is required for every cancellation. Repeated last-minute cancellations or no-shows may
          affect your ability to book in the future.</p>
        </Section>

        <Section n="4" title="Warranties">
          <p>Eligible services may carry a limited service warranty as indicated at the time of booking. The warranty
          covers only the specific work performed and excludes pre-existing faults, misuse, third-party tampering, or
          issues unrelated to the original service. To claim warranty support, contact us within the stated warranty
          window with your booking details.</p>
        </Section>

        <Section n="5" title="Liabilities">
          <p>To the maximum extent permitted by law, Fixigo's total liability for any claim arising out of a service is
          limited to the amount paid by you for that specific service. Fixigo is not liable for indirect, incidental, or
          consequential damages. Nothing in these Terms excludes any liability that cannot be excluded under applicable
          law.</p>
        </Section>

        <Section n="6" title="Customer Responsibilities">
          <p>You agree to provide a safe working environment, accurate address and contact details, and access to the
          premises and equipment required for the service. You are responsible for securing valuables and informing us
          of any hazards at the service location.</p>
        </Section>

        <Section n="7" title="Governing Law">
          <p>These Terms are governed by the laws of India and any disputes are subject to the exclusive jurisdiction of
          the courts of Bengaluru, Karnataka.</p>
        </Section>

        <Section n="8" title="Contact Us">
          <p>For any questions about these Terms or a cancellation, reach us at
          <strong> support@fixigohomeservices.in</strong> or <strong>+91 91529 43297</strong> (call or WhatsApp).</p>
        </Section>

        <p className="mt-10 text-xs text-[#94a3b8] border-t border-[#E2E8F0] pt-6">
          This document is a general template provided for convenience and does not constitute legal advice. Please have
          it reviewed by a qualified legal professional before relying on it.
        </p>
      </div>
    </StoreLayout>
  );
}
